import { useQuery } from '@tanstack/react-query';
import {
  SimpleGrid, Card, Group, Text, Center, Loader, Alert, Stack, Table,
} from '@mantine/core';
import { Star, Skull } from 'lucide-react';

import { gamesApi, epochsApi } from '../api';
import { Leaderboard } from '../components/Leaderboard';
import { Eyebrow } from '../components/Eyebrow';
import { GroupTag } from '../components/GroupTag';
import { numStyle } from '../theme';
import { plural } from '../utils/format';

export default function StatsPage() {
  // Statistiky se nikdy nemíchají přes epochy. MVP si aktivní epochu bere server sám,
  // u skóre se musí poslat výslovně — bez epoch_id by sečetlo všechny tábory dohromady.
  const epochs = useQuery({ queryKey: ['epochs'], queryFn: epochsApi.getAll });
  const activeEpoch = epochs.data?.find((e) => e.is_active === 1) ?? null;

  const mvp = useQuery({ queryKey: ['mvpOverall'], queryFn: gamesApi.getOverallMvpStats });
  const scores = useQuery({
    queryKey: ['scoresOverall', activeEpoch?.id],
    queryFn: () => gamesApi.getOverallScores(activeEpoch!.id),
    enabled: !!activeEpoch,
  });

  if (epochs.isLoading || mvp.isLoading) return <Center h={240}><Loader /></Center>;

  if (!activeEpoch) {
    return (
      <Alert color="orange" title="Žádná aktivní epocha">
        Statistiky se počítají vždy za jednu epochu. Aktivuj ji v Nastavení → Epochy.
      </Alert>
    );
  }

  const mvpVotes = mvp.data?.overallVotes ?? [];
  const deaths = scores.data ?? [];

  return (
    <Stack gap="sm">
      {(mvp.error || scores.error) && (
        <Alert color="orange" title="Část statistik se nenačetla">
          Zobrazujeme, co se povedlo získat. Zkus obnovit stránku.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="sm">
        <Card padding={0}>
          <Group p="sm" gap={8} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Star size={15} color="var(--mantine-color-yellow-6)" />
            <Text fw={700} fz="sm" style={{ flex: 1 }}>MVP · {activeEpoch.name}</Text>
            <Eyebrow>{mvp.data?.strategy === 'per_group' ? 'vítěz skupiny' : 'klasické MVP'}</Eyebrow>
          </Group>
          {mvpVotes.length === 0 ? (
            <Text c="dimmed" ta="center" py="lg" fz="sm">Zatím nikdo nehlasoval.</Text>
          ) : (
            <Leaderboard
              unit="hl."
              rows={mvpVotes.map((v) => ({
                key: v.memberNumber,
                name: `${v.memberNumber} · ${v.memberName}`,
                sub: `${v.groupName} · ${plural(v.gamesVoted, 'hra', 'hry', 'her')}`,
                value: v.totalVotes,
                color: v.groupColor,
              }))}
            />
          )}
        </Card>

        <Card padding={0}>
          <Group p="sm" gap={8} style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Skull size={15} color="var(--mantine-color-red-6)" />
            <Text fw={700} fz="sm" style={{ flex: 1 }}>Mrtvoliště · {activeEpoch.name}</Text>
          </Group>
          {scores.isLoading ? (
            <Center h={120}><Loader size="sm" /></Center>
          ) : deaths.length === 0 ? (
            <Text c="dimmed" ta="center" py="lg" fz="sm">Zatím nikdo nezemřel.</Text>
          ) : (
            <Table.ScrollContainer minWidth={420}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={56}>Číslo</Table.Th>
                    <Table.Th>Člen</Table.Th>
                    <Table.Th>Skupina</Table.Th>
                    <Table.Th w={70} ta="right">Smrtí</Table.Th>
                    <Table.Th w={100} ta="right">Celkem</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {deaths.map((s) => (
                    <Table.Tr key={s.member_number}>
                      <Table.Td><Text style={numStyle} fz="sm" fw={600}>{s.member_number}</Text></Table.Td>
                      <Table.Td fz="sm" fw={600}>{s.name ?? 'Bez jména'}</Table.Td>
                      <Table.Td><GroupTag name={s.group_name} color={s.group_color} /></Table.Td>
                      <Table.Td ta="right"><Text style={numStyle} fz="sm">{s.death_count}×</Text></Table.Td>
                      <Table.Td ta="right"><Text style={numStyle} fz="sm">{s.total_minutes} min</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
      </SimpleGrid>

      {!!mvp.data?.groupReceivedStats?.length && (
        <Card padding={0}>
          <Group p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
            <Text fw={700} fz="sm">Hlasy podle skupin</Text>
          </Group>
          <Leaderboard
            unit="hl."
            rows={mvp.data.groupReceivedStats.map((g) => ({
              key: g.groupId,
              name: g.groupName,
              value: g.totalVotesReceived,
              color: g.groupColor,
            }))}
          />
        </Card>
      )}
    </Stack>
  );
}
